import React, { useEffect, useRef, useState } from "react"
import cardStyle from "../../../styles/CardCategory.module.css"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitleChildrenPath, TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import {
  faAward,
  faCaretRight,
  faClose,
  faGifts,
  faMoneyBill,
  faPenToSquare,
  faPlus,
  faRemove,
  faTags,
} from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import GetSpace from "../../../utils/space"
import { PersonComponent } from "../../../components/Person/PersonComponent"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { useError } from "../../../context/useErrorContext"
import { CustomerEntity } from "../../../Objects/Customer/CustomerEntity"
import { useSaveCustomer } from "../../../hooks/Costumer/useSaveCustomer"
import { useCreateCustomer } from "../../../hooks/Costumer/useCreateCustomer"
import { useGetCustomer } from "../../../hooks/Costumer/useGetCustomer"
import { ListSelections } from "../../../components/ListSelections/ListSelections"
import { awardMemory } from "../../../Objects/Memory/Memory"
import { CountElements } from "../../../components/CountElements/CountElements"
import { useToggle } from "../../../hooks/useToggle"
import { ToggleComponet } from "../../../components/Toggle/ToggleComponet"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { EAutoType } from "../../../Enum/EAutoType"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { EOldType } from "../../../Enum/EOldType"
import { EMoneyConditionType } from "../../../Enum/EMoneyConditionType"
import { AwardManagerEntity } from "../../../Objects/Awards/AwardManagerEntity"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { BasicCard } from "../../../components/BasicCard/BasicCard"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { CanView } from "../../../components/CanView/CanView"
import { AddressEntity } from "../../../Objects/Person/AddressEntity"
import { AddressesManager } from "../../../components/Address/AddressesManager"
import { ECustomerPaymentType } from "../../../Enum/ECustomerPaymentType"
import { ExitCheckResponse, useCheckCustomerExit } from "../../../hooks/Costumer/useCheckCustomerExit"
import { CustomerDebitPopup } from "./CustomerDebitPopup"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"

interface CustomerPopupProbs {
  title: string
  showAll?: boolean
  costumer?: CustomerEntity | undefined | null
  onClonse: () => void
}

export const CustomerPopup: React.FC<CustomerPopupProbs> = ({ title, costumer, showAll = true, onClonse }) => {
  const { t } = useTranslation("customer")

  const [addresses, setAddresses] = useState<AddressEntity[]>(() => costumer?.personEntity?.addresses ?? [])

  const [isSearchingDebit, setIsSearchingDebit] = useState(false)

  const [debitCheckResult, setDebitCheckResult] = useState<ExitCheckResponse | null>(null)

  const [showDebitModal, setShowDebitModal] = useState(false)

  const canEdite = !usePermission(Permissions.CanEditeClients)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const [enableAwards, setAwards] = useState(false)

  const [awardManagers, setAwardManager] = useState<AwardManagerEntity[]>(() => {
    if (costumer) {
      return costumer?.awardManagers
    }

    return []
  })

  const [awardsIds, setAwardsIds] = useState<number[]>(() => {
    if (costumer) {
      return costumer?.awardsIds
    }

    const tempAward: number[] = []

    awardMemory.listData.forEach((ss) => {
      if (ss.isAutomatic === EAutoType.Auto) tempAward.push(ss.id)
    })

    return tempAward
  })

  const valuesToggle = Object.mapEnumToStrings(EOldType)

  const { selectedType, handleToggleSelection } = useToggle([
    costumer?.oldType.toOldTypeString() === valuesToggle[0] ? valuesToggle[0] : valuesToggle[1],
  ])

  const { setNewConfirmation } = useConfirmation()

  const { handleGetCostumer } = useGetCustomer()

  const { setError } = useError()

  const { handleInitialValue, handleSubmitForm } = useCreateCustomer()

  const personRef = useRef<any>(null)

  const { handleSavesCostumer } = useSaveCustomer()

  const { handleCheckCustomerExit } = useCheckCustomerExit()

  const handleAddAwards = (e: number) => {
    const awardToAdd = awardMemory.findById(e)

    if (!awardToAdd || awardToAdd.status === EEnableStatusType.Inactive) {
      setError(t("components:status_inactive"), t("awards:label_aready_associate_description"))
      return
    }

    if (awardsIds.filter((ss) => ss === e).length > 0 || awardManagers.some((ss) => ss.awardId === e)) {
      setError(t("label_aready_associate_name"), t("label_aready_associate_description"))

      return
    }

    const awardsTemp: number[] = [...awardsIds, awardToAdd.id]
    setAwardsIds(awardsTemp)
    setAwards(false)
  }

  useEffect(() => {
    handleInitialValue(costumer)
  }, [costumer])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const person = personRef.current?.validate()

    if (!person) {
      event.currentTarget.reportValidity()

      return
    }

    person.addresses = addresses

    const customer = handleSubmitForm(person)

    if (!customer) {
      event.currentTarget.reportValidity()

      return
    }

    customer.personEntity = person

    customer.awardsIds = Array.from(new Set([...awardsIds, ...awardManagers.map((e) => e.awardId)]))

    customer.oldType =
      selectedType[0] === EOldType.IsChildren.toOldTypeString() ? EOldType.IsChildren : EOldType.IsAdult

    handleSavesCostumer(customer, () => {
      onClonse()

      handleGetCostumer(() => {})
    })
  }

  const handleRemove = (e: number) => {
    setAwardManager((prev) => prev.filter((a) => a.awardId !== e))

    const temp: number[] = [...awardsIds.filter((ss) => ss !== e)]

    setAwardsIds(temp)
  }

  const handleChangeType = (e: string[]) => {}

  const handleFindCustomerDebit = async () => {
    const cpf = costumer?.personEntity?.cpf?.trim()

    if (!cpf) {
      setError(t("customer_debit.cpf_missing_title"), t("customer_debit.cpf_missing_description"))
      return
    }

    setIsSearchingDebit(true)

    await handleCheckCustomerExit(cpf, setDebitCheckResult)

    setIsSearchingDebit(false)

    setShowDebitModal(true)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faPenToSquare}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.fluxo.customer.customerpopup.1"
                    variant="global"
                    title={t("helpPopup:global.customer.title")}
                    description={t("helpPopup:global.customer.description")}
                    bullets={t("helpPopup:global.customer.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClonse}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.fluxo.customer.customerPopup.button.onClonse"
                  />
                </div>
              }
            />

            <form onSubmit={handleSubmit}>
              <div className="w-full mt-2">
                <ToggleComponet
                  canSelectType={true}
                  selectedType={selectedType}
                  valuesToggle={valuesToggle}
                  handleChangeType={handleChangeType}
                  handleToggleSelection={handleToggleSelection}
                  disable={canEdite}
                  descriptionKey="interactionDescriptions:pages.fluxo.customer.customerPopup.toggle.toggle"
                />

                <div className={defaultStyle.divisionBar}></div>

                <PersonComponent
                  basic={!showAll}
                  initExpasive={true}
                  personEntity={costumer?.personEntity}
                  ref={personRef}
                  disable={canEdite}
                />
              </div>

              {showAll && (
                <>
                  <div className={defaultStyle.divisionBar}></div>

                  <AddressesManager value={addresses} onChange={setAddresses} disable={canEdite} />
                </>
              )}

              <div className={defaultStyle.divisionBar}></div>

              <TitleChildrenPath
                title={t("awards:label_associate_awards_customer")}
                icon={faAward}
                boxShadow="none"
                iconColor={THEME.colors.iconBGColor}
                subtitleChildren={
                  <div className="flex items-center gap-2">
                    <PopupHelpButton
                      helpKey="pages.fluxo.customer.customerpopup.2"
                      title={t("helpPopup:sections.customer.awards.title")}
                      description={t("helpPopup:sections.customer.awards.description")}
                      bullets={t("helpPopup:sections.customer.awards.bullets", { returnObjects: true }) as string[]}
                    />

                    <ButtonIcon
                      disabled={canEdite}
                      sizeNumber={SIZE.buttonExpandIcon}
                      bgColor={THEME.colors.buttonBGPlusColor}
                      iconColor={THEME.colors.buttonFGPlusColor}
                      icon={faPlus}
                      onClick={() => setAwards(true)}
                      size="small"
                      descriptionKey="interactionDescriptions:pages.fluxo.customer.customerPopup.button.setAwardsTrue"
                    />
                  </div>
                }
                children={
                  <>
                    <GetSpace top={5} />

                    <div>
                      {(awardsIds.length > 0 || awardManagers) && (
                        <>
                          <CountElements
                            count={awardsIds.length + awardManagers.length}
                            title={t("awards:label_associate_awards_count_customer")}
                            boxShadow="none"
                          />

                          <GetSpace top={5} />

                          {awardManagers.map((e, index) => {
                            var award = awardMemory.findById(e.awardId)

                            const isInactive = award?.status === EEnableStatusType.Inactive

                            return (
                              <div
                                key={index}
                                className={`rounded-lg p-2 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${
                                  isInactive
                                    ? "border border-red-200 bg-gradient-to-br from-red-50 via-white to-red-100/40"
                                    : "border border-orange-100 bg-gradient-to-br from-white via-orange-50 to-orange-100/40"
                                }`}
                              >
                                <div className={cardStyle.card}>
                                  <div className={cardStyle.cardCollum}>
                                    <div className={cardStyle.cardContent}>
                                      <span className={cardStyle.cardTitle} style={{ width: "100%" }}>
                                        {award.name}
                                      </span>

                                      {isInactive && (
                                        <span className="mr-2 inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-[11px] font-semibold text-red-700">
                                          {t("components:status_inactive_short")}
                                        </span>
                                      )}

                                      <BasicCard
                                        blink={e.hasAwardToTake ? true : false}
                                        title={
                                          !e.hasAwardToTake
                                            ? award.isAutomatic === EAutoType.Auto
                                              ? t("types:select_automatic")
                                              : t("types:select_not_automatic")
                                            : t("awards:label_win")
                                        }
                                        width="auto"
                                        borderColor="rgb(0, 0, 0)"
                                      />

                                      <GetSpace left={5} />

                                      <ButtonIcon
                                        disabled={canEdite || isInactive}
                                        sizeNumber={SIZE.buttonExpandIcon}
                                        bgColor={THEME.colors.buttonBGRemoveColor}
                                        iconColor={THEME.colors.buttonFGRemoveColor}
                                        icon={faRemove}
                                        onClick={(e: any) =>
                                          setNewConfirmation(
                                            t("awards:label_title_remove"),
                                            t("awards:label_description_remove"),
                                            () => handleRemove(award?.id!),
                                          )
                                        }
                                        size="small"
                                        descriptionKey="interactionDescriptions:pages.fluxo.customer.customerPopup.button.titleRemove"
                                      />
                                    </div>

                                    <GetSpace top={5} />

                                    <span
                                      className={cardStyle.cardTxtBody1}
                                      style={{ marginLeft: "50px", width: "100%" }}
                                    >
                                      {award.description.truncate(40)}
                                    </span>
                                  </div>

                                  <GetSpace top={15} />

                                  <div className={cardStyle.cardContent}>
                                    <div className={cardStyle.cardContent}>
                                      <FontAwesomeIcon
                                        icon={faMoneyBill}
                                        style={{ marginLeft: "10px", fontSize: "14px" }}
                                      />

                                      <span className={cardStyle.cardMoneyTxt} style={{ width: "100%" }}>
                                        {t("awards:label_min_consume_amount")}
                                      </span>
                                    </div>

                                    <GetSpace left={5} />

                                    <BasicCard
                                      width="auto"
                                      title={e.currentCount.toString() + "/" + award?.countToWin.toString()}
                                      borderColor="rgb(0, 0, 0)"
                                    />
                                  </div>

                                  <GetSpace top={5} />

                                  <div className={cardStyle.cardContent}>
                                    <div className={cardStyle.cardContent}>
                                      <FontAwesomeIcon icon={faTags} style={{ marginLeft: "10px", fontSize: "14px" }} />

                                      <span className={cardStyle.cardMoneyTxt} style={{ width: "100%" }}>
                                        {t("awards:label_discount_apply")}
                                      </span>
                                    </div>

                                    <GetSpace left={5} />

                                    <BasicCard
                                      title={
                                        award?.conditionType === EMoneyConditionType.FixedValue
                                          ? award?.valueDiscountWhenWon.toString().toMoney()
                                          : award?.valueDiscountWhenWon + "%"
                                      }
                                      borderColor="rgb(0, 0, 0)"
                                    />
                                  </div>

                                  <GetSpace top={5} />

                                  <div className={cardStyle.cardContent}>
                                    <div className={cardStyle.cardContent}>
                                      <FontAwesomeIcon
                                        icon={faGifts}
                                        style={{ marginLeft: "10px", fontSize: "14px" }}
                                      />

                                      <span className={cardStyle.cardMoneyTxt} style={{ width: "100%" }}>
                                        {t("awards:label_time_wins")}
                                      </span>
                                    </div>

                                    <GetSpace left={5} />

                                    <BasicCard title={e.timeWon.toString()} borderColor="rgb(255, 110, 110)" />
                                  </div>
                                </div>

                                <div className={defaultStyle.divisionBar}></div>
                              </div>
                            )
                          })}

                          {awardsIds.map((e, index) => {
                            var award = awardMemory.findById(e)

                            const isInactive = award?.status === EEnableStatusType.Inactive

                            return (
                              <div
                                key={index}
                                className={`rounded-lg p-2 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${
                                  isInactive
                                    ? "border border-red-200 bg-gradient-to-br from-red-50 via-white to-red-100/40"
                                    : "border border-orange-100 bg-gradient-to-br from-white via-orange-50 to-orange-100/40"
                                }`}
                              >
                                <GetSpace top={3} />

                                <TitlePageResponsiveIconSmall
                                  title={`${award?.name}${isInactive ? ` • ${t("components:status_inactive_short")}` : ""}`}
                                  icon={faCaretRight}
                                  iconColor={THEME.colors.iconBGSecondaryColor}
                                  boxShadow="none"
                                  sizeProp="1x"
                                  children={
                                    <ButtonIcon
                                      size="small"
                                      icon={faRemove}
                                      disabled={canEdite || isInactive}
                                      sizeNumber={SIZE.buttonExpandIcon}
                                      bgColor={THEME.colors.buttonBGRemoveColor}
                                      iconColor={THEME.colors.buttonFGRemoveColor}
                                      onClick={handleRemove}
                                      descriptionKey="interactionDescriptions:pages.fluxo.customer.customerPopup.button.handleRemove"
                                    />
                                  }
                                />
                              </div>
                            )
                          })}
                        </>
                      )}
                    </div>
                  </>
                }
              />

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeClients}>
                  <Button
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    label={t("buttons:button_save")}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.fluxo.customer.customerPopup.button.save"
                  />
                </CanView>

                {costumer && costumer.id !== -1 && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      typeButton="button"
                      type="primary"
                      size="medium"
                      label={t("buttons:button_register")}
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      onClick={handleEnableRegister}
                      descriptionKey="interactionDescriptions:pages.fluxo.customer.customerPopup.button.register"
                    />
                  </>
                )}
              </div>

              <GetSpace top={5} />

              <div className={defaultStyle.buttonsController}>
                {costumer?.status === ECustomerPaymentType.WithInDebit && (
                  <Button
                    typeButton="button"
                    type="primary"
                    size="medium"
                    label={isSearchingDebit ? t("customer_debit.button_loading") : t("customer_debit.button_default")}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    disabled={isSearchingDebit}
                    onClick={() => void handleFindCustomerDebit()}
                    descriptionKey="interactionDescriptions:pages.fluxo.customer.customerPopup.button.loading"
                  />
                )}
              </div>
            </form>
          </div>
        </Animate>

        {isRegisterEnable && (
          <ViewRegister id={costumer?.id!} typeEntity="CustomerEntityId" onClose={handleDisableRegister} />
        )}

        {enableAwards && (
          <ListSelections
            onClinck={handleAddAwards}
            icon={faAward}
            title={t("awards:label_title_select_award")}
            messageCount={t("list:label_awards_page_message_count")}
            messageEmpty={t("list:label_awards_page_message_empty")}
            listCards={awardMemory.getListCards(true)}
            onClonse={() => setAwards(false)}
          />
        )}

        {showDebitModal && <CustomerDebitPopup data={debitCheckResult} onClose={() => setShowDebitModal(false)} />}
      </div>
    </ModalPortal>
  )
}
